import DOMUpdateEvent from "../../helpers/dom_update_event";
import { getElementDocumentPosition, cumulativeOffset, getDocumentScrollPosition } from "../../helpers/element_in_viewport";
import verge from 'verge';
import { config as popoverWindowCloseboxConfig, popoverWindowClosebox } from "./popover_window_closebox"
import { config as popoverWindowPointerConfig, popoverWindowPointer } from "./popover_window_pointer"

export const config = {
    baseHtmlClass: 'popover_window',
    linerHtmlClass: 'popover_window__liner',
    contentHtmlClass: 'popover_window__content',
    contentSelector: '.popover_window__content',
    pointerSelector: '.popover_window__pointer',
    loadingModifier: 'loading',
    width: 256
}

export class popoverWindow {

    static createElement(type=null) {
        let element = document.createElement('div');
        element.setAttribute("class", popoverWindow.htmlClasses(type));
        element.appendChild(popoverWindowPointer.createElement());

        let linerElement = document.createElement('div');
        linerElement.setAttribute('class', config.linerHtmlClass);

        let closeboxElement = popoverWindowClosebox.createElement();
        linerElement.appendChild(closeboxElement);

        let contentElement = document.createElement('div');
        contentElement.setAttribute('class', config.contentHtmlClass);
        linerElement.appendChild(contentElement);

        element.appendChild(linerElement);
        return element;
    }

    static htmlClasses(type = null) {
        const baseClass = config.baseHtmlClass;
        let classes = [ baseClass ];
        if (type) {
            classes.push(`${ baseClass }--${ type }`);
        };
        return classes.join(' ');
    }

    constructor(popover) {
        this.config = config;
        this.popover = popover; // The parent popover
        this.type = this.getType();
        this.width = this.getWidth();
        this.zIndex = this.getZIndex();
        this.closeboxes = [];
    }

    open(x, y) {
        this.x = x;
        this.y = y;
        this.insertElement();
        this.boundCenterBoxHandler = this.centerBox.bind(this);
        window.addEventListener('resize', this.boundCenterBoxHandler);
    }

    close() {
        window.removeEventListener('resize', this.boundCenterBoxHandler);
        this.removeElement();
    }

    setContent(content) {
        this.contentElement.innerHTML = content;
        new DOMUpdateEvent().fire();
    }

    render() {
        this.centerBox();
        this.showCloseboxes();
    }

    // private

    getType() {
        return this.popover.getType();
    }

    getWidth() {
        let width = this.popover.getBoxWidth();
        if (width == null) { width = this.config.width };
        return width;
    }

    getZIndex() {
        return this.popover.getBoxZIndex();
    }

    insertElement() {
        this.element = popoverWindow.createElement(this.type);
        document.body.appendChild(this.element);
        this.contentElement = this.getContentElement();
        this.pointerElement = this.getPointerElement();
        this.applyWidth();
        this.applyZIndex();
        window.requestAnimationFrame(() => {
            this.centerBox();
        })
        this.initializeCloseboxes();
    }

    applyWidth() {
        this.contentElement.style.width = this.width;
        // this.contentElement.setStyle({ width: this.width });
    }

    applyZIndex() {
        this.contentElement.style.zIndex = this.zIndex;
    }

    centerBox() {
        try {
            // Get viewport dimensions
            let viewport_width = verge.viewportW();
            let viewport_height = verge.viewportH();

            // Get box dimensions
            let box_width = this.element.offsetWidth;
            let box_height = this.element.offsetHeight;

            // Get popover dimensions and position
            let popover_width = this.popover.element.offsetWidth;
            let popover_height = this.popover.element.offsetHeight;
            let popoverOffsetLeft = (-(box_width / 2) + (popover_width / 2));
            let popoverOffsetTop = popover_height;
            let popoverPosition = getElementDocumentPosition(this.popover.element);

            // Initial positioning
            this.element.style.top = `${ popoverPosition.top + popoverOffsetTop }px`;
            this.element.style.left = `${ popoverPosition.left + popoverOffsetLeft }px`;

            // Get the current position of the box
            let box_left = cumulativeOffset(this.element).left;
            let box_top = cumulativeOffset(this.element).top;
            let box_right = box_left + box_width;
            let box_bottom = box_top + box_height;

            // Get the document scroll position
            let scrollPosition = getDocumentScrollPosition();

            // Calculate if the box is outside the viewport
            let isOutsideRight = box_right > viewport_width + scrollPosition.left;
            let isOutsideLeft = box_left < scrollPosition.left;
            let isOutsideBottom = box_bottom > viewport_height + scrollPosition.top;
            let isOutsideTop = box_top < scrollPosition.top;

            // HORIZONTAL ADJUSTMENTS

            // Adjust the width depending on whether it fits in the viewport
            if (box_width >= viewport_width) {
                this.element.style.right = '0px';
                this.element.style.left = '0px';
                this.contentElement.style.width = 'auto';
            } else {
                this.element.style.left = box_left + 'px';
                this.element.style.right = 'auto';
            }

            // Adjust the horizontal position if the box is outside the viewport
            if (isOutsideLeft && isOutsideRight) {
                this.element.style.left = '0px';
                this.element.style.right = '0px';
            } else if (isOutsideLeft) {
                this.element.style.left = '0px';
                this.element.style.right = 'auto';
            } else if (isOutsideRight) {
                this.element.style.left = 'auto';
                this.element.style.right = '0px';
            } else {
                this.element.style.left = box_left + 'px';
                this.element.style.right = 'auto';
            }

            // VERTICAL ADJUSTMENTS

            // If the box is outside the bottom of the viewport
            if (isOutsideBottom) {
                // Position the popover above the trigger element instead of below it
                let newTopPosition = popoverPosition.top - box_height;

                // Check if positioning above would put it outside the top of the viewport
                if (newTopPosition < scrollPosition.top) {
                    // If it would go outside the top, keep it below but adjust to fit in viewport
                    this.element.style.top = `${viewport_height + scrollPosition.top - box_height}px`;
                } else {
                    // Otherwise position it above the trigger
                    this.element.style.top = `${newTopPosition}px`;

                    // Flip the pointer to point down instead of up
                    let pointer_el = this.element.querySelector(this.config.pointerSelector);
                    if (pointer_el) {
                        pointer_el.style.top = `${box_height - pointer_el.offsetHeight}px`;
                        pointer_el.classList.add('popover_window__pointer--flipped');
                    }
                }
            } else if (isOutsideTop) {
                // If it's outside the top, position it at the top of the viewport
                this.element.style.top = `${scrollPosition.top}px`;
            }

            // Adjust the horizontal position of the pointer
            let pointer_el = this.element.querySelector(this.config.pointerSelector);
            if (pointer_el) {
                // Recalculate box position after all adjustments
                box_left = cumulativeOffset(this.element).left;
                let popover_left = cumulativeOffset(this.popover.element).left;
                let pointer_offset = popover_left + (popover_width / 2) - box_left - (pointer_el.offsetWidth / 2);

                // Ensure pointer stays within the bounds of the popover
                pointer_offset = Math.max(pointer_el.offsetWidth, Math.min(box_width - pointer_el.offsetWidth, pointer_offset));
                pointer_el.style.left = pointer_offset + 'px';
            }
        } catch(e){
            console.error('Error in centerBox:', e);
            this.center_on_point(this.x,this.y);
        } // Handle exceptions somewhat gracefully
    }

    center_on_point(x,y) {
        let offset_left = (-(this.element.offsetWidth / 2));
        let offset_top = (this.element.offsetHeight);
        this.element.style.top = (y+offset_top) + "px";
        this.element.style.left = (x+offset_left) + "px";
    }

    removeElement() {
        try {
            if (this.element) { this.element.parentNode.removeChild(this.element) };
        } catch(e){}
    }

    getContentElement(){
        return this.element.querySelector(config.contentSelector);
    }

    getPointerElement(){
        return this.element.querySelector(popoverWindowPointerConfig.selector);
    }

    initializeCloseboxes(){
        this.getCloseboxNodes().forEach( (node) => {
            this.closeboxes.push( new popoverWindowClosebox(node, this) );
        } );
    }

    getCloseboxNodes() {
        return this.element.querySelectorAll(popoverWindowCloseboxConfig.selector);
    }

    showCloseboxes() {
        this.closeboxes.forEach( closebox => closebox.show() );
    }

    closePopover(event) {
        this.popover.doClose(event);
    }

    addLoadingState() {
        this.addState(this.config.loadingModifier)
    }

    removeLoadingState() {
        this.removeState(this.config.loadingModifier)
    }

    // Helpers

    hasState(state) {
        return this.element.classList.contains(`${this.config.baseHtmlClass}--${state}`);
    }

    addState(state) {
        this.element.classList.add(`${this.config.baseHtmlClass}--${state}`);
    }

    removeState(state) {
        this.element.classList.remove(`${this.config.baseHtmlClass}--${state}`);
    }

}

